/**
 * URL state behind the Peers page property filter (one search box, AWS-console style).
 *
 * Tokens combine with AND across properties; several values of ONE property combine with OR
 * (`Region = US, EU`). Every key stays in the query string so a refresh or a pasted link lands
 * on the same view, and the default (no filter) leaves the URL clean.
 *
 * - `region`   — comma list of region groups (`US,EU`). A single value is the old URL shape.
 * - `enrolled` — a relative preset (`1d` / `7d` / `30d`), resolved against "now" on read.
 * - `dtr`      — the devices tab's transport (`relay`), see `vpnTransport.ts`.
 * - `dq` / `q` — free text: the devices tab's server search / the person tabs' local search.
 */

export const REGION_PARAM = 'region';
export const ENROLLED_PARAM = 'enrolled';

/** `US,EU` → ['US', 'EU']; blanks and repeats dropped, order kept. */
export function parseList(raw?: string | null): string[] {
  if (!raw) return [];
  return [...new Set(raw.split(',').map((s) => s.trim()).filter(Boolean))];
}

/** The URL value for a list, or null (= delete the key) when empty. */
export function serializeList(values: string[]): string | null {
  return values.length ? values.join(',') : null;
}

/** Add the value when absent, remove it when present. */
export function toggleValue(values: string[], v: string): string[] {
  return values.includes(v) ? values.filter((x) => x !== v) : [...values, v];
}

export const ENROLLED_PRESETS = [
  { value: '1d', label: 'Last 24 hours', ms: 24 * 3600 * 1000 },
  { value: '7d', label: 'Last 7 days', ms: 7 * 24 * 3600 * 1000 },
  { value: '30d', label: 'Last 30 days', ms: 30 * 24 * 3600 * 1000 },
] as const;

export type EnrolledPreset = (typeof ENROLLED_PRESETS)[number]['value'];

/** Anything but a known preset is "no filter": a hand-edited URL must not 400 the query. */
export function parseEnrolled(raw?: string | null): EnrolledPreset | null {
  return ENROLLED_PRESETS.find((p) => p.value === raw)?.value ?? null;
}

/**
 * The cut-off instant for a preset, floored to the minute so the query variables (and the
 * Apollo cache key) change at most once a minute instead of on every render.
 */
export function enrolledSince(preset: EnrolledPreset | null, now: number = Date.now()): string | null {
  const p = ENROLLED_PRESETS.find((x) => x.value === preset);
  if (!p) return null;
  const minute = Math.floor(now / 60_000) * 60_000;
  return new Date(minute - p.ms).toISOString();
}

/**
 * Whether the device query must wait. A region filter names groups (`US`); the query needs the
 * raw values behind them, which come from `vpnDeviceRegionCounts`. Until that has answered, the
 * filter cannot be expressed, and running the query anyway lists the WHOLE fleet first (a full
 * `mdm-devices` scan in prod, plus rail reads for a row the filter excludes). A region with no
 * devices has no raw values either: also skip, never fall back to "all".
 */
export function skipDeviceQuery(regions: string[], rawRegions: string[], regionCountsLoaded: boolean): boolean {
  return regions.length > 0 && (!regionCountsLoaded || rawRegions.length === 0);
}
